import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { BackButton } from '@/components/shared/back-button';
import { cn } from '@/lib/utils';
import type { BuilderCourse, CourseStatus } from '@/types';

const STATUS_LABELS: Record<CourseStatus, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
};

const STATUS_COLORS: Record<CourseStatus, string> = {
  draft:
    'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-400/20',
  published:
    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-400/20',
  archived:
    'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-400/20',
};

type Props = {
  course: BuilderCourse;
  onStatusChange: (status: CourseStatus) => void;
  onDelete?: () => void;
  statusPending: boolean;
  deletePending?: boolean;
  statusError: Error | null;
};

export function CourseHeader({
  course,
  onStatusChange,
  onDelete,
  statusPending,
  deletePending,
  statusError,
}: Props) {
  const isDraft = course.status === 'draft';
  const [confirmDelete, setConfirmDelete] = useState(false);
  const hasEnrollments = (course._count?.enrollments ?? 0) > 0;

  return (
    <div className="mb-6">
      <BackButton fallback="/instructor/courses" label="Volver a mis capacitaciones" />
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-foreground">{course.title}</h2>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_COLORS[course.status],
              )}
            >
              {STATUS_LABELS[course.status]}
            </span>
          </div>
          {course.description && (
            <p className="mt-1 text-sm text-muted-foreground">{course.description}</p>
          )}
          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span>Nota min: {course.passing_grade}%</span>
            <span>Intentos: {course.max_attempts}</span>
            <span>Inscritos: {course._count?.enrollments ?? 0}</span>
            {course.category && <span>Categoria: {course.category}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <button
              onClick={() => onStatusChange('published')}
              disabled={statusPending}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
            >
              Publicar
            </button>
          )}
          {course.status === 'published' && (
            <button
              onClick={() => onStatusChange('archived')}
              disabled={statusPending}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
            >
              Archivar
            </button>
          )}
          {course.status === 'archived' && (
            <button
              onClick={() => onStatusChange('draft')}
              disabled={statusPending}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
            >
              Reactivar
            </button>
          )}
          {isDraft && onDelete && !hasEnrollments && (
            <>
              {confirmDelete ? (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-1.5">
                  <span className="text-xs text-destructive">Confirmar eliminacion?</span>
                  <button
                    onClick={() => {
                      onDelete();
                      setConfirmDelete(false);
                    }}
                    disabled={deletePending}
                    className="rounded bg-destructive px-2 py-1 text-xs font-medium text-white hover:bg-destructive/90 disabled:opacity-50"
                  >
                    {deletePending ? 'Eliminando...' : 'Si, eliminar'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg border border-destructive/30 p-2 text-destructive transition-colors hover:bg-destructive/10"
                  aria-label="Eliminar curso"
                  title="Eliminar curso"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {statusError && <p className="mt-2 text-xs text-destructive">{statusError.message}</p>}
    </div>
  );
}
